/**
 * The idle game's shell (D240 to D246): the coins, the income and the barn along the top,
 * the screen, and a tab bar of World, Almanac, Festival and Region. The old game's screens
 * are not shown for an idle world (D244); archived classic worlds are listed in settings and
 * do not open (D241).
 */
import type { ComponentChildren, JSX } from 'preact';
import { useEffect, useState } from 'preact/hooks';
import { now } from '../clock.ts';
import {
  idleHeritageBonus,
  idleIncome,
  idleLandTraitIds,
  idleSeason,
  idleUpgradesVisible,
  type IdleIntent,
  type IdleWelcomeBack,
} from '@hedgerow/sim';
import { useSystemDark } from '../hooks.ts';
import {
  CHANNEL_LABEL,
  Counter,
  Fanfare,
  flyCoins,
  installAudioUnlock,
  JuiceLayer,
  type MomentFact,
  playSound,
  popNumber,
  SOUND_CHANNELS,
  type SoundChannel,
  useMasterVolume,
  useSoundChannel,
  useSoundMuted,
} from '../juice/index.ts';
import { Button } from '../ui/Button.tsx';
import { hasSpriteIcon, Icon } from '../ui/Icon.tsx';
import { useShell } from './shell.ts';
import { GUIDE_CONTENTS_PAGE } from '../game/copy.ts';
import { GuideScreen } from '../screens/GuideScreen.tsx';
import { Segmented } from '../ui/Segmented.tsx';
import { Figs } from './BuyButton.tsx';
import { Sheet } from '../ui/Sheet.tsx';
import { RenameWorldSheet } from './RenameWorldSheet.tsx';
import { ArrangeBar } from './ArrangeUI.tsx';
import { TabBar, type TabDef } from '../ui/TabBar.tsx';
import { AlmanacTab } from './AlmanacTab.tsx';
import { WorldTab } from './WorldTab.tsx';
import { WorldList } from './WorldList.tsx';
import { Switch } from './Switch.tsx';
import {
  KeyIcon,
  SETTINGS_LABEL,
  SETTINGS_NOTE,
  FULL_SHEET,
  SettingsHead,
  SettingsNavRow,
  SettingsRow,
  SoundRow,
} from './SettingsRows.tsx';
import { traitLine } from './WorldRows.tsx';
import { FestivalPending, FestivalTab } from './FestivalTab.tsx';
import { openSignIn } from '../game/signin-bus.ts';
import { SignInScreen, useAccount } from '../screens/SignInScreen.tsx';
import { FirstRun } from './FirstRun.tsx';
import { Landing } from './Landing.tsx';
import { useDefaultWorld } from './world-pref.ts';
import { InstallPrompt, InstallSetting } from './InstallPrompt.tsx';
import { SecurePrompt, SecureSetting } from './SecurePrompt.tsx';
import { DeleteAccountButton } from './DeleteAccount.tsx';
import { NotificationsSetting } from './NotificationsSetting.tsx';
import { RegionTab } from './RegionTab.tsx';
import { orderPrompt, ordersReady, welcomeHead } from './daily-view.ts';
import { arriveByTab, useBadges } from './tab-ui.tsx';
import { stepShort } from './tree-view.ts';
import { moreOffersText, offerStrips, stackStrips, type OfferStrip } from './offers.ts';
import { coins, num, useScientific } from './notation.ts';
import { cap, t as term, type TermKey } from './terms.ts';
import { useFlatMap, useShowSigns } from './three/pref.ts';
import {
  MAP_QUALITIES,
  MAP_QUALITY_LABEL,
  MAP_QUALITY_WORDS,
  useMapQuality,
  type MapQuality,
} from './map-quality.ts';
import { idleContent, useIdle, type IdleTab } from './store.ts';
import {
  barnChip,
  barnOffer,
  clockAt,
  bonusText,
  worldPrompt,
  fmtSpan,
  markName,
  nextLine,
  rateText,
  refusalText,
  upgradeCards,
  weatherText,
  welcomeFinds,
  welcomeGoalsSummary,
  welcomeNext,
  welcomeVisitors,
  seasonName,
  yearHeader,
  yearOfGeneration,
} from './view.ts';

const TICK_MS = 1000;

/**
 * The sim's `IdleIntent` types an argument-free intent as `{ type } & Record<string, never>`,
 * which no object literal satisfies; the cast is the wire shape exactly.
 */
const UPGRADE_BARN: IdleIntent = { type: 'upgradeBarn' };

/**
 * The tabs in the shown world's theme (D348): its own names, and its own icons where the theme
 * draws them (`packages/art/themes/{theme}/tabs/`, named for the tab), the world's otherwise.
 */
export function idleTabs(): TabDef[] {
  const tab = (id: string, key: TermKey, glyph: string): TabDef => {
    const label = term(key);
    const own = `tabs_${label.toLowerCase().replace(/[^a-z0-9]/g, '')}`;
    return { id, label, icon: hasSpriteIcon(own) ? own : glyph };
  };
  return [
    tab('world', 'tabWorld', 'land-plot'),
    tab('almanac', 'tabAlmanac', 'book'),
    tab('festival', 'tabFestival', 'sun'),
    tab('region', 'tabRegion', 'users'),
  ];
}

function TopStrip() {
  const world = useIdle((s) => s.world);
  const worldName = useIdle((s) => s.worldName);
  const t = useIdle((s) => s.now);
  const banked = useIdle((s) => s.banked);
  useScientific();
  if (!world) return null;
  const rate = idleIncome(world, idleContent, t);
  const barn = barnChip(world, idleContent, t, banked);
  // D431: the chip holds the time it is full at; the whole sentence is its name and tooltip,
  // and the barn sheet (a tap) says the rest.
  const barnText = barn.short;
  // D441, UI step 2: the season is named in the header's line; the ring and dial are gone.
  const seasonWord = seasonName(idleContent, idleSeason(world, idleContent, t));
  // The world's name, then its year line, then the coins and the rate on the left; the barn and
  // Settings on the right.
  return (
    <header
      style={{
        display: 'flex',
        alignItems: 'center',
        gap: 8,
        padding: '0 14px',
        paddingTop: 'env(safe-area-inset-top)',
        minHeight: 'calc(48px + env(safe-area-inset-top))',
        boxSizing: 'border-box',
        background: 'var(--bg)',
        flex: 'none',
      }}
    >
      <div style={{ display: 'grid', gap: 1, minWidth: 0, flex: '1 1 auto' }}>
        {worldName && (
          <span
            data-testid="world-name"
            title={worldName}
            style={{
              font: '600 13px/1.25 var(--font-ui)',
              color: 'var(--ink)',
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              whiteSpace: 'nowrap',
              minWidth: 0,
            }}
          >
            {worldName}
          </span>
        )}
        <span
          style={{
            font: '500 12px/1.25 var(--font-ui)',
            color: 'var(--ink-2)',
            whiteSpace: 'nowrap',
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            minWidth: 0,
          }}
        >
          <span data-testid="year">{yearHeader(world)}</span>
          <span data-testid="season"> · {seasonWord}</span>
        </span>
        {/* D342, item 247: coins, then the rate, on one line at every width (D459). The rate never shortens or
            wraps. D441: with no rate yet the line is in ink-2, not the green of a rising
            income. */}
        <span
          style={{
            display: 'flex',
            flexWrap: 'nowrap',
            alignItems: 'baseline',
            gap: '0 8px',
            minWidth: 0,
          }}
        >
          <Counter
            value={world.year.coins}
            rate={rate}
            format={coins}
            coinTarget
            style={{
              font: '700 28px/1.1 var(--font-num)',
              color: 'var(--ink)',
              // The coin bump scales the counter to 108% from its left edge; this keeps a gap.
              marginRight: 8,
            }}
          />
          <span
            data-testid="income"
            title={rate > 0 ? `+${rateText(rate)}` : 'Tap to earn'}
            style={{
              font: `${rate > 0 ? 600 : 500} 13px/1.2 var(${rate > 0 ? '--font-num' : '--font-ui'})`,
              color: rate > 0 ? 'var(--green)' : 'var(--ink-2)',
              flex: 'none',
              whiteSpace: 'nowrap',
            }}
          >
            {rate > 0 ? `+${rateText(rate)}` : 'Tap to earn'}
          </span>
        </span>
      </div>
      <button
        type="button"
        data-testid="barn-chip"
        aria-label={barn.label}
        title={barn.label}
        onClick={() => useIdle.getState().setBarnOpen(true)}
        // The button is a 44px target; the 32px chip is drawn inside it.
        style={{
          height: 44,
          padding: 0,
          border: 0,
          background: 'transparent',
          display: 'inline-flex',
          alignItems: 'center',
          color: 'var(--ink)',
          cursor: 'pointer',
          flex: 'none',
        }}
      >
        <span
          style={{
            height: 32,
            padding: '0 10px',
            display: 'inline-flex',
            alignItems: 'center',
            gap: 5,
            borderRadius: 'var(--radius-md)',
            border: `1px solid ${barn.full ? 'var(--red)' : 'var(--line)'}`,
            background: barn.full ? 'var(--red-bg)' : 'var(--surface)',
            font: '600 12px/1 var(--font-ui)',
            whiteSpace: 'nowrap',
            boxSizing: 'border-box',
          }}
        >
          <Icon name="warehouse" size={16} />
          <span
            style={{ overflow: 'hidden', textOverflow: 'ellipsis', maxWidth: 150, flex: 'none' }}
          >
            {barnText.split(/(\d[\d:]*[hms]?)/).map((part, i) =>
              i % 2 ? (
                <span key={i} class="tnum">
                  {part}
                </span>
              ) : (
                part
              ),
            )}
          </span>
        </span>
      </button>
      <Button
        variant="ghost"
        aria-label="Settings"
        onClick={() => useIdle.getState().setSettingsOpen(true)}
        style={{
          width: 44,
          minWidth: 44,
          height: 44,
          flexShrink: 0,
          padding: 0,
          margin: '0 -8px 0 -4px',
          color: 'var(--ink-2)',
        }}
      >
        <Icon name="settings" size={20} />
      </Button>
    </header>
  );
}

/**
 * D258, D285: one line under the header with the nearest goal, or the festival, and how far
 * it has come. A tap goes to where it is met: the Festival tab, the Almanac tab, or the row
 * on the World tab. On the World tab itself this is the one prompt bar: `worldPrompt` ranks the
 * festival (when it is a good time to hold it), Heritage to spend and an upgrade ready above
 * it, so none of the three ever stacks with another.
 */
function NextStrip({ pill = false }: { pill?: boolean }) {
  const world = useIdle((s) => s.world);
  const tab = useIdle((s) => s.tab);
  useScientific();
  if (!world) return null;
  const readyUpgrade =
    tab === 'world'
      ? upgradeCards(world, idleContent, idleUpgradesVisible(world, idleContent)).onShow.find(
          (c) => c.needs === undefined,
        )
      : undefined;
  const line =
    tab === 'world'
      ? worldPrompt(
          world,
          idleContent,
          world.advancedTo,
          null,
          readyUpgrade ? stepShort(idleContent, readyUpgrade.id) : null,
          orderPrompt(ordersReady(world, idleContent, world.advancedTo)),
          readyUpgrade ? `${term('tabAlmanac')} · ${coins(readyUpgrade.cost)}` : null,
        )
      : nextLine(world, idleContent);
  const ready = line.kind === 'festival_ready' || line.kind === 'upgrade' || line.kind === 'order';
  // D460: the strip is a surface strip in every state; a ready one takes the accent on its icon and link.
  const go = () => {
    useIdle.getState().setTab(line.tab);
    const id = line.testId;
    if (!id) return;
    // After the World tab has drawn: bring the row into view.
    requestAnimationFrame(() =>
      requestAnimationFrame(() =>
        document
          .querySelector(`[data-testid="${id}"]`)
          ?.scrollIntoView({ block: 'center', behavior: 'smooth' }),
      ),
    );
  };
  const pct = Math.floor(line.progress * 100);
  const action =
    line.tab === 'almanac'
      ? `Open ${term('tabAlmanac')}`
      : line.tab === 'region'
        ? `Open ${term('tabRegion')}`
        : `Open ${term('tabFestival')}`;
  const testId =
    line.kind === 'order'
      ? 'order-ready'
      : line.kind === 'upgrade'
        ? 'upgrade-ready'
        : ready
          ? 'festival-ready'
          : 'next-goal';
  return (
    <div
      style={
        pill
          ? {
              // D431: a 36px pill on the map; the coin colour when it is ready.
              position: 'relative',
              display: 'flex',
              alignItems: 'stretch',
              minHeight: 36,
              ...(line.detail ? { minHeight: 56 } : {}),
              boxSizing: 'border-box',
              borderRadius: 'var(--radius-md)',
              background: 'var(--surface)',
              border: '1px solid var(--line)',
              boxShadow: '0 2px 10px rgba(0, 0, 0, 0.25)',
              pointerEvents: 'auto',
            }
          : {
              // D460, section 17: a 40px inset strip, not a full-width band.
              position: 'relative',
              display: 'flex',
              alignItems: 'stretch',
              minHeight: 40,
              boxSizing: 'border-box',
              margin: '6px var(--edge)',
              borderRadius: 'var(--radius-md)',
              background: 'var(--surface)',
              border: '1px solid var(--line)',
            }
      }
    >
      <button
        type="button"
        data-testid={testId}
        onClick={go}
        aria-label={ready ? `${line.text}. ${action}` : `Next: ${line.text}, ${pct}% of the way`}
        style={{
          flex: 1,
          display: 'grid',
          gridTemplateColumns: 'auto minmax(0, 1fr) auto',
          alignItems: 'center',
          columnGap: 8,
          rowGap: 4,
          minHeight: 44,
          // In the pill the 44px target reaches 4px past the 36px drawing, top and bottom.
          ...(pill && !line.detail ? { margin: '-5px 0' } : {}),
          ...(!pill && !line.detail ? { margin: '-2px 0' } : {}),
          ...(line.detail ? { minHeight: 56 } : {}),
          padding: pill ? '0 10px 0 12px' : '0 10px 3px 12px',
          border: 0,
          background: 'transparent',
          color: 'var(--ink)',
          font: '500 13px/1.25 var(--font-ui)',
          textAlign: 'left',
          cursor: 'pointer',
        }}
      >
        <span style={{ display: 'inline-flex', color: ready ? 'var(--accent)' : 'inherit' }}>
          <Icon
            name={
              line.kind === 'upgrade'
                ? 'book'
                : ready || line.kind === 'festival'
                  ? 'sun'
                  : 'clipboard-list'
            }
            size={16}
          />
        </span>
        <span
          title={ready ? line.text : `Next: ${line.text}`}
          data-testid="strip-copy"
          style={{ minWidth: 0, display: 'grid', gap: 2, overflowWrap: 'anywhere' }}
        >
          <span>
            {ready ? (
              line.text
            ) : (
              <>
                <span style={{ fontWeight: 600 }}>Next:</span> {line.text}
              </>
            )}
          </span>
          {line.detail && (
            <span
              data-testid="strip-detail"
              style={{
                font: '400 var(--fs-note)/1.25 var(--font-ui)',
                color: 'var(--ink-2)',
              }}
            >
              <Figs text={line.detail} />
            </span>
          )}
        </span>
        {ready ? (
          <span
            style={{
              font: '600 13px/1 var(--font-ui)',
              color: 'var(--accent)',
              display: 'inline-flex',
              alignItems: 'center',
              gap: 2,
            }}
          >
            {action}
            {pill && (
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" aria-hidden="true">
                <path
                  d="m9 6 6 6-6 6"
                  stroke="currentColor"
                  stroke-width="2.4"
                  stroke-linecap="round"
                  stroke-linejoin="round"
                />
              </svg>
            )}
          </span>
        ) : (
          <span class="num" style={{ font: '500 12px/1 var(--font-num)', color: 'var(--ink-2)' }}>
            {pct}%
          </span>
        )}
        {
          <div
            class="juice-bar"
            role="progressbar"
            aria-hidden="true"
            style={{
              position: 'absolute',
              left: 14,
              right: 14,
              bottom: 2,
              height: '3px',
              borderRadius: '3px',
            }}
          >
            <span style={{ background: 'var(--accent)', transform: `scaleX(${line.progress})` }} />
          </div>
        }
      </button>
    </div>
  );
}

/** The strip's right-hand button when something is ready: an `on-coin` ring on the coin fill. */
const RING_BUTTON = {
  minWidth: 'var(--buy-w)',
  minHeight: 44,
  padding: '0 14px',
  borderRadius: 'var(--radius-md)',
  border: '2px solid var(--on-coin)',
  background: 'transparent',
  color: 'var(--on-coin)',
  font: '600 13px/1 var(--font-ui)',
  cursor: 'pointer',
} as const;

/**
 * D442: an offer or bonus as a strip like the goal strip: `surface`, a 3px accent foot, the
 * icon in the accent, the sentence over its timer, and a 44px Claim or Collect on the right.
 * Something ready to collect takes the `coin` fill and `on-coin` text, its button a ring.
 */
function OfferStripView({
  strip,
  pill = false,
  testId,
}: {
  strip: OfferStrip;
  pill?: boolean;
  testId?: string;
}) {
  const { ready } = strip;
  const claim = (e: MouseEvent) => {
    const el = e.currentTarget as Element;
    const r = useIdle.getState().act({ type: 'claimBonus', offer: strip.id });
    if (!r.ok) {
      playSound('error');
      popNumber(el, refusalText(idleContent, r));
      return;
    }
    if (strip.kind === 'visitor') {
      playSound('coin');
      flyCoins(el, { count: 6 });
      popNumber(el, strip.detail.split(',')[0]!);
      return;
    }
    playSound('milestone');
    popNumber(el, 'Claimed');
  };
  return (
    <div
      data-testid={testId ?? strip.testId}
      style={{
        position: 'relative',
        display: 'flex',
        alignItems: 'center',
        gap: 8,
        minHeight: 52,
        padding: '4px 8px 7px 12px',
        boxSizing: 'border-box',
        borderRadius: 'var(--radius-md)',
        background: ready ? 'var(--coin)' : 'var(--surface)',
        color: ready ? 'var(--on-coin)' : 'var(--ink)',
        border: ready ? '1px solid var(--coin-edge)' : '1px solid var(--line)',
        overflow: 'hidden',
        font: '500 13px/1.25 var(--font-ui)',
        ...(pill ? { boxShadow: '0 2px 10px rgba(0, 0, 0, 0.25)', pointerEvents: 'auto' } : {}),
      }}
    >
      <span
        style={{
          color: ready ? 'var(--on-coin)' : 'var(--accent)',
          display: 'inline-flex',
          flex: 'none',
        }}
      >
        <Icon name={strip.icon} size={18} />
      </span>
      <span style={{ flex: 1, minWidth: 0, display: 'grid', gap: 2 }}>
        <span
          style={{
            display: '-webkit-box',
            WebkitBoxOrient: 'vertical',
            WebkitLineClamp: 2,
            overflow: 'hidden',
          }}
        >
          {strip.text}
        </span>
        <span
          style={{
            font: '500 var(--fs-note)/1.2 var(--font-ui)',
            color: ready ? 'var(--on-coin)' : 'var(--ink-2)',
          }}
        >
          <Figs text={strip.detail} />
        </span>
      </span>
      {strip.action &&
        (ready ? (
          <button type="button" style={RING_BUTTON} onClick={claim}>
            {strip.action}
          </button>
        ) : (
          <Button variant="primary" style={{ flex: 'none' }} onClick={claim}>
            {strip.action}
          </Button>
        ))}
      <span
        aria-hidden="true"
        style={{
          position: 'absolute',
          left: 0,
          right: 0,
          bottom: 0,
          height: 3,
          background: ready ? 'var(--coin-edge)' : 'var(--accent)',
        }}
      />
    </div>
  );
}

/** The strips in force now, most urgent first, redrawn on the clock's tick. */
function useOfferStrips(): OfferStrip[] {
  const world = useIdle((s) => s.world);
  const t = useIdle((s) => s.now);
  useScientific();
  return world ? offerStrips(world, idleContent, t) : [];
}

/** The 44px "N more offers" button: dark glass on the map, a plain secondary button elsewhere. */
export function MoreOffersButton({ n, glass = false }: { n: number; glass?: boolean }) {
  if (n <= 0) return null;
  return (
    <button
      type="button"
      data-testid="offers-more"
      class={glass ? 'idle-chrome' : undefined}
      onClick={() => useIdle.getState().setOffersOpen(true)}
      style={{
        minHeight: 44,
        padding: '0 14px',
        display: 'inline-flex',
        alignItems: 'center',
        justifyContent: 'center',
        gap: 4,
        borderRadius: 'var(--radius-md)',
        border: glass ? '1px solid rgba(255, 255, 255, 0.18)' : '1px solid var(--line)',
        background: glass ? undefined : 'var(--surface)',
        color: glass ? '#fff' : 'var(--ink)',
        font: '600 13px/1 var(--font-ui)',
        whiteSpace: 'nowrap',
        cursor: 'pointer',
        pointerEvents: 'auto',
      }}
    >
      {moreOffersText(n)}
      <svg width="16" height="16" viewBox="0 0 24 24" fill="none" aria-hidden="true">
        <path
          d="m9 6 6 6-6 6"
          stroke="currentColor"
          stroke-width="2.4"
          stroke-linecap="round"
          stroke-linejoin="round"
        />
      </svg>
    </button>
  );
}

/**
 * D244, D442: the strips under the goal strip. The most urgent offer shows; on the map the
 * rest sit behind the button at the map's foot (`MoreOffersMap`), elsewhere behind the same
 * button in this stack.
 */
function BonusStrip({ pill = false }: { pill?: boolean }) {
  const strips = useOfferStrips();
  const { shown, more } = stackStrips(strips);
  if (shown.length === 0) return null;
  return (
    <div
      data-testid="bonus-strip"
      style={
        pill
          ? { display: 'grid', gap: 6 }
          : {
              display: 'grid',
              gap: 6,
              padding: '6px 12px',
              background: 'var(--bg)',
              borderBottom: '1px solid var(--line)',
              flex: 'none',
            }
      }
    >
      {shown.map((s) => (
        <OfferStripView key={s.id} strip={s} pill={pill} />
      ))}
      {!pill && more.length > 0 && (
        <div>
          <MoreOffersButton n={more.length} />
        </div>
      )}
    </div>
  );
}

/** The map's foot button, between the compass and the overflow button. */
export function MoreOffersMap() {
  const strips = useOfferStrips();
  return <MoreOffersButton n={stackStrips(strips).more.length} glass />;
}

/** D442: every offer and bonus as a strip, in the map's order. */
function OffersSheet() {
  const open = useIdle((s) => s.offersOpen);
  const strips = useOfferStrips();
  if (!open) return null;
  const close = () => useIdle.getState().setOffersOpen(false);
  return (
    <Sheet
      align="bottom"
      title="Offers and bonuses"
      icon="store"
      onClose={close}
      testId="offers-sheet"
      zIndex={40}
    >
      {strips.length === 0 ? (
        <p style={{ margin: 0, color: 'var(--ink-2)' }}>Nothing on offer.</p>
      ) : (
        <div style={{ display: 'grid', gap: 8 }}>
          {strips.map((s) => (
            <OfferStripView key={s.id} strip={s} testId={`${s.testId}-in-sheet`} />
          ))}
        </div>
      )}
    </Sheet>
  );
}

/** The barn: how long it holds, and the upgrade (D245: the cap is shown and can be raised). */
function BarnUpgrade({
  onDone,
  welcome,
}: {
  onDone?: () => void;
  welcome?: IdleWelcomeBack | null;
}) {
  const world = useIdle((s) => s.world);
  if (!world) return null;
  const offer = barnOffer(world, idleContent, welcome);
  if (offer.cost === null || !offer.nextHolds)
    return <p style={{ margin: 0 }}>The {term('barn')} is as big as it gets.</p>;
  const reason = offer.needs !== undefined ? `Needs ${coins(offer.needs)}` : undefined;
  return (
    <>
      <Button
        variant="primary"
        full
        {...(reason ? { reason } : {})}
        onClick={(e) => {
          const r = useIdle.getState().act(UPGRADE_BARN);
          if (!r.ok) {
            playSound('error');
            return;
          }
          playSound('buy');
          flyCoins(e.currentTarget as Element, { count: 4 });
          onDone?.();
        }}
      >
        Upgrade to hold {offer.nextHolds}, {coins(offer.cost)}
      </Button>
      {offer.wouldHaveKept !== undefined && (
        <p style={{ margin: 0 }} data-testid="barn-would-have-kept">
          Would have kept {coins(offer.wouldHaveKept)} more
        </p>
      )}
    </>
  );
}

function BarnSheet() {
  const open = useIdle((s) => s.barnOpen);
  const world = useIdle((s) => s.world);
  const welcome = useIdle((s) => s.welcome ?? s.barnWelcome);
  const t = useIdle((s) => s.now);
  if (!open || !world) return null;
  const barn = barnChip(world, idleContent, t);
  const close = () => useIdle.getState().setBarnOpen(false);
  // D459: the barn's facts as a ruled two-column list; no icon in the title.
  const facts: Array<[string, string, string]> = [
    ['Holds', fmtSpan(barn.capacity), 'barn-holds'],
    ['Full at', clockAt(barn.fullAt, t), 'barn-fills'],
    [
      'After that',
      barn.overflow > 0
        ? `Keeps ${Math.round(barn.overflow * 100)}% of income`
        : 'Stops until you are back',
      'barn-after',
    ],
  ];
  return (
    <Sheet
      align="bottom"
      title={`The ${term('barn')}`}
      onClose={close}
      testId="barn-sheet"
      zIndex={40}
    >
      <p style={{ margin: 0 }} data-testid="barn-overflow">
        While you are away the {term('world')} earns into the {term('barn')}. While you play, it
        earns without limit.
      </p>
      <ul class="juice-moment-facts">
        {facts.map(([label, value, id]) => (
          <li key={id} data-testid={id}>
            <span class="juice-moment-label">{label}</span>
            <span class="tnum juice-moment-value">{value}</span>
          </li>
        ))}
      </ul>
      <BarnUpgrade welcome={welcome} />
    </Sheet>
  );
}

/**
 * D280: a new version is installed and waiting (D47's updater raises `updateReady`). Shown
 * under the header, or at the top over the first run and the loading screen, so nothing in
 * the idle app stands between the player and an update. Update reloads into it.
 */
function UpdateBar({ top }: { top: boolean }) {
  const updateReady = useShell((s) => s.updateReady);
  if (!updateReady) return null;
  return (
    <div
      data-testid="update-bar"
      role="status"
      style={{
        position: 'relative',
        zIndex: top ? 21 : undefined,
        display: 'flex',
        alignItems: 'center',
        gap: 8,
        padding: '6px 12px',
        ...(top ? { paddingTop: 'calc(6px + env(safe-area-inset-top))' } : {}),
        background: 'var(--surface)',
        borderBottom: '1px solid var(--line)',
        font: '600 14px/1.3 var(--font-ui)',
        flex: 'none',
      }}
    >
      <Icon name="refresh-cw" size={18} />
      <span style={{ flex: 1 }}>A new version is ready</span>
      <Button variant="primary" onClick={() => void useShell.getState().applyUpdate()}>
        Update
      </Button>
    </div>
  );
}

/**
 * D358: a channel's row on the Sound settings page: its label, its own volume slider and
 * its off switch, one tap away. The slider sits greyed and disabled once off. D458: the slider
 * is the 6px track with an accent fill and a 24px knob (`.idle-range`), in a 44px tall target.
 */
function VolumeRow({
  label,
  volume,
  off,
  onVolume,
  onOff,
  testId,
}: {
  label: string;
  volume: number;
  off: boolean;
  onVolume: (v: number) => void;
  onOff: (v: boolean) => void;
  testId: string;
}) {
  const pct = Math.round(volume * 100);
  return (
    <div style={{ display: 'grid', gap: 0, alignContent: 'center' }} data-testid={testId}>
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
        <span style={SETTINGS_LABEL}>{label}</span>
        <Switch label={label} on={!off} onChange={(v) => onOff(!v)} />
      </div>
      <input
        type="range"
        class="idle-range"
        min={0}
        max={100}
        step={5}
        value={pct}
        disabled={off}
        aria-label={`${label} volume`}
        data-testid={`${testId}-volume`}
        onInput={(e) => onVolume(Number((e.currentTarget as HTMLInputElement).value) / 100)}
        style={{ '--pct': `${pct}%`, opacity: off ? 0.4 : 1 } as JSX.CSSProperties}
      />
    </div>
  );
}

/** D358: one of the four channels below master, each its own row. */
function SoundChannelRow({ channel }: { channel: SoundChannel }) {
  const [{ volume, off }, { setVolume, setOff }] = useSoundChannel(channel);
  return (
    <VolumeRow
      label={CHANNEL_LABEL[channel]}
      volume={volume}
      off={off}
      onVolume={setVolume}
      onOff={setOff}
      testId={`sound-channel-${channel}`}
    />
  );
}

/**
 * D358: the Sound settings page. Master, with its own volume and the one-tap-away off
 * switch (unchanged since D244), then the four channels below it: taps and buttons,
 * rewards, menus and map ambience. D459: it replaces Settings in the same full-bleed sheet, with
 * Back and no Close, and the title centred.
 */
function SoundSettingsSheet({ onBack }: { onBack: () => void }) {
  const [muted, setMuted] = useSoundMuted();
  const [masterVolume, setMasterVolume] = useMasterVolume();
  return (
    <Sheet
      align="bottom"
      title={<span style={{ display: 'block', textAlign: 'center' }}>Sound</span>}
      ariaLabel="Sound"
      titleAction={<span aria-hidden="true" style={{ width: 34, flex: 'none' }} />}
      onBack={onBack}
      testId="sound-settings"
      zIndex={40}
      style={FULL_SHEET}
    >
      <div class="idle-set-group">
        <VolumeRow
          label="Sound"
          volume={masterVolume}
          off={muted}
          onVolume={setMasterVolume}
          onOff={setMuted}
          testId="sound-master"
        />
      </div>
      <p style={{ margin: 0, ...SETTINGS_NOTE }}>
        Sound off silences everything; the levels are kept for when you turn it back on.
      </p>
      <div class="idle-set-group">
        {SOUND_CHANNELS.map((c) => (
          <SoundChannelRow key={c} channel={c} />
        ))}
      </div>
    </Sheet>
  );
}

/** D458: "Signed in": a guest is not secured yet; an account says how it signs in. */
function AccountRow({ onOpen }: { onOpen: () => void }) {
  const { account } = useAccount();
  const secured = !!account && !account.isGuest;
  return (
    <SettingsNavRow
      testId="settings-account-button"
      icon={<KeyIcon />}
      label="Signed in"
      onClick={onOpen}
      note={
        secured ? (
          <span
            data-testid="settings-account"
            style={{ color: 'var(--green)', display: 'inline-flex', alignItems: 'center', gap: 4 }}
          >
            <Icon name="check" size={14} />
            {account.email ?? 'With a passkey'}
          </span>
        ) : (
          <span data-testid="settings-account">
            <span
              style={{ color: 'var(--ink)', display: 'inline-flex', alignItems: 'center', gap: 6 }}
            >
              <span
                aria-hidden="true"
                style={{ width: 8, height: 8, borderRadius: '50%', background: 'var(--amber)' }}
              />
              Not secured yet
            </span>
            <br />
            Add a passkey so you can sign in on another phone
          </span>
        )
      }
    />
  );
}

const ASK_EACH_TIME = 'Ask each time';

/** "Open on start": the choices, as a pushed view with Back to Settings (as Sound is). */
function OpenOnStartSheet({
  worlds,
  value,
  onPick,
  onBack,
}: {
  worlds: { worldId: string; name: string }[];
  value: string | null;
  onPick: (id: string | null) => void;
  onBack: () => void;
}) {
  const choices = [{ worldId: '', name: ASK_EACH_TIME }, ...worlds];
  return (
    <Sheet
      align="bottom"
      title={<span style={{ display: 'block', textAlign: 'center' }}>Open on start</span>}
      ariaLabel="Open on start"
      titleAction={<span aria-hidden="true" style={{ width: 34, flex: 'none' }} />}
      onBack={onBack}
      testId="open-on-start-sheet"
      zIndex={40}
      style={FULL_SHEET}
    >
      <div class="idle-set-group" role="radiogroup" aria-label="Open on start">
        {choices.map((c) => {
          const on = (value ?? '') === c.worldId;
          return (
            <button
              key={c.worldId}
              type="button"
              class="idle-set-nav"
              role="radio"
              aria-checked={on}
              data-testid={`open-on-start-${c.worldId || 'ask'}`}
              onClick={() => onPick(c.worldId || null)}
            >
              <span style={{ ...SETTINGS_LABEL, flex: 1, textAlign: 'left' }}>{c.name}</span>
              {on && <Icon name="check" size={18} />}
            </button>
          );
        })}
      </div>
    </Sheet>
  );
}

const PRIVACY_LINES: { title: string; text: string }[] = [
  {
    title: 'What is stored',
    text: 'Your email, if you sign in, to send sign-in links. Your worlds and their progress. Play stats against an anonymous key, to balance the game.',
  },
  {
    title: 'Who sees it',
    text: 'Nothing is sold or shared. Mail goes through Resend. The game runs on Cloudflare.',
  },
  {
    title: 'Deleting',
    text: 'Deleting a world or your account removes everything linked to you. Anonymous play stats are kept to balance the game.',
  },
];

function PrivacySheet({ onBack }: { onBack: () => void }) {
  return (
    <Sheet
      align="bottom"
      title={<span style={{ display: 'block', textAlign: 'center' }}>Privacy</span>}
      ariaLabel="Privacy"
      titleAction={<span aria-hidden="true" style={{ width: 34, flex: 'none' }} />}
      onBack={onBack}
      testId="privacy-sheet"
      zIndex={40}
      style={FULL_SHEET}
    >
      <div class="idle-set-group">
        {PRIVACY_LINES.map((l) => (
          <div
            key={l.title}
            style={{
              display: 'grid',
              gap: 2,
              alignContent: 'center',
              minHeight: 48,
              padding: '8px 12px',
            }}
          >
            <span style={SETTINGS_LABEL}>{l.title}</span>
            <span style={{ font: '400 var(--fs-body)/1.4 var(--font-ui)', color: 'var(--ink)' }}>
              {l.text}
            </span>
          </div>
        ))}
      </div>
    </Sheet>
  );
}

function SettingsSheet() {
  const open = useIdle((s) => s.settingsOpen);
  const worlds = useIdle((s) => s.worlds);
  const worldName = useIdle((s) => s.worldName);
  // D344: the world's kind and land traits, as stored with its layout.
  const layout = useIdle((s) => s.world?.layout);
  // Item 275: "Your land" names what the world is like. A kind or trait the content no longer
  // names would leave the row empty, so the row shows only when it has words.
  const landLine = traitLine(idleLandTraitIds(layout), idleContent.theme.id);
  const [muted, setMuted] = useSoundMuted();
  const [sci, setSci] = useScientific();
  const [flatMap, setFlatMap] = useFlatMap();
  const [showSigns, setShowSigns] = useShowSigns();
  const [mapQuality, setMapQuality] = useMapQuality();
  const [startWorld, setStartWorld] = useDefaultWorld();
  const [renaming, setRenaming] = useState(false);
  const [soundOpen, setSoundOpen] = useState(false);
  const [startOpen, setStartOpen] = useState(false);
  const [privacyOpen, setPrivacyOpen] = useState(false);
  if (!open) return null;
  const playable = worlds.filter((f) => !f.archived);
  const startName = playable.find((f) => f.worldId === startWorld)?.name ?? ASK_EACH_TIME;
  const closeSettings = () => useIdle.getState().setSettingsOpen(false);
  // D459: Sound is a pushed view, so it takes Settings' place; Back returns to it.
  if (soundOpen) return <SoundSettingsSheet onBack={() => setSoundOpen(false)} />;
  if (privacyOpen) return <PrivacySheet onBack={() => setPrivacyOpen(false)} />;
  if (startOpen) {
    return (
      <OpenOnStartSheet
        worlds={playable}
        value={startWorld}
        onPick={(id) => {
          setStartWorld(id);
          setStartOpen(false);
        }}
        onBack={() => setStartOpen(false)}
      />
    );
  }
  return (
    <Sheet
      align="bottom"
      title="Settings"
      onClose={closeSettings}
      testId="idle-settings"
      zIndex={40}
      style={FULL_SHEET}
    >
      {/* D458 (section 15): Your worlds, Game, Map, Account, then Delete account last. Grouped
          lists, 12px corners and 48px rows ruled between; a switch for on and off, the small
          segmented control for a choice. */}
      <div style={{ display: 'grid', gap: 8 }}>
        <WorldList worlds={worlds} />
        {worlds.filter((f) => !f.archived).length > 1 && (
          <div class="idle-set-group">
            <SettingsNavRow
              testId="open-on-start"
              label="Open on start"
              value={startName}
              onClick={() => setStartOpen(true)}
            />
          </div>
        )}
      </div>
      <SettingsHead title="Game" />
      <div class="idle-set-group">
        {/* D358: master off stays one tap away from Settings; the mixer is its own page. */}
        <SoundRow on={!muted} onToggle={(v) => setMuted(!v)} onOpen={() => setSoundOpen(true)} />
        <SettingsRow label="Big numbers">
          <Segmented
            variant="inRow"
            value={sci ? 'sci' : 'short'}
            onChange={(v) => setSci(v === 'sci')}
            options={[
              { value: 'short', label: '1.2M' },
              { value: 'sci', label: '1.2e6' },
            ]}
          />
        </SettingsRow>
        <NotificationsSetting />
        <InstallSetting />
        <SecureSetting />
        <SettingsNavRow
          label="How to play"
          onClick={() => useShell.getState().openGuide(GUIDE_CONTENTS_PAGE)}
        />
        <SettingsNavRow testId="privacy" label="Privacy" onClick={() => setPrivacyOpen(true)} />
      </div>
      <SettingsHead title="Map" />
      <div class="idle-set-group">
        <SettingsRow label={MAP_QUALITY_LABEL} testId="map-quality" stack>
          <Segmented<MapQuality>
            variant="inRow"
            value={mapQuality}
            onChange={setMapQuality}
            style={{ width: '100%' }}
            options={MAP_QUALITIES.map((q) => ({ value: q, label: MAP_QUALITY_WORDS[q] }))}
          />
        </SettingsRow>
        <SettingsRow label="Flat map" note={`A flat view of the ${term('world')}`}>
          <Switch label="Flat map" on={flatMap} onChange={setFlatMap} />
        </SettingsRow>
        <SettingsRow label="Level signs and bursts">
          <Switch label="Level signs and bursts" on={showSigns} onChange={setShowSigns} />
        </SettingsRow>
      </div>
      <SettingsHead title="Account" />
      <div class="idle-set-group">
        <AccountRow onOpen={openSignIn} />
        <SettingsNavRow label="World name" value={worldName} onClick={() => setRenaming(true)} />
        {landLine && (
          <div
            style={{ display: 'grid', gap: 2, alignContent: 'center', padding: '8px 12px' }}
            data-testid="settings-land"
          >
            <span style={SETTINGS_LABEL}>Your land</span>
            <span style={{ font: '400 var(--fs-body)/1.4 var(--font-ui)', color: 'var(--ink)' }}>
              {landLine}
            </span>
          </div>
        )}
      </div>
      <DeleteAccountButton />
      <p style={{ margin: 0, ...SETTINGS_NOTE, textAlign: 'center' }}>
        Idle Worlds <span data-testid="version">{__APP_VERSION__}</span>
        {' · '}Motion and dark mode follow your phone.
      </p>
      {renaming && (
        <RenameWorldSheet name={worldName} worlds={worlds} onClose={() => setRenaming(false)} />
      )}
    </Sheet>
  );
}

/** D244: the festival's own fanfare: the Heritage earned, the mark, and the new year. */
function FestivalFanfare() {
  const record = useIdle((s) => s.festival);
  const world = useIdle((s) => s.world);
  useEffect(() => {
    if (record) playSound('festival');
  }, [record]);
  if (!record || !world) return null;
  const bonus = idleHeritageBonus(idleContent, world.permanent.heritageEarned);
  return (
    <Fanfare
      title={`Harvest ${term('festival')}`}
      amount={`+${num(record.heritage)} ${term('heritage')}`}
      testId="festival-fanfare"
      facts={[
        { label: cap(term('heritage')), value: num(world.permanent.heritage) },
        { label: 'Income bonus', value: bonusText(bonus) },
        { label: 'New mark', value: cap(markName(record.mark)) },
      ]}
      onClose={() => useIdle.getState().closeFestival()}
      actions={
        <Button variant="primary" full onClick={() => useIdle.getState().closeFestival()}>
          Start year {yearOfGeneration(world)}
        </Button>
      }
    />
  );
}

/** D461: a left-aligned 44px link row in the accent with a chevron. */
function LinkRow({
  children,
  onClick,
  testId,
}: {
  children: ComponentChildren;
  onClick: () => void;
  testId?: string;
}) {
  return (
    <button
      type="button"
      data-testid={testId}
      onClick={onClick}
      style={{
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        minHeight: 44,
        width: '100%',
        padding: 0,
        border: 0,
        background: 'transparent',
        color: 'var(--accent)',
        textAlign: 'left',
        cursor: 'pointer',
        font: '600 var(--fs-body)/1.3 var(--font-ui)',
      }}
    >
      <span>{children}</span>
      <svg
        width="16"
        height="16"
        viewBox="0 0 24 24"
        fill="none"
        stroke="currentColor"
        stroke-width="2"
        stroke-linecap="round"
        stroke-linejoin="round"
        aria-hidden="true"
      >
        <path d="m9 6 6 6-6 6" />
      </svg>
    </button>
  );
}

function Welcome() {
  const w = useIdle((s) => s.welcome);
  const world = useIdle((s) => s.world);
  const goals = useIdle((s) => s.welcomeGoals);
  const landing = useIdle((s) => s.landingOpen);
  useEffect(() => {
    if (w && !landing) playSound('welcome');
  }, [w, landing]);
  if (!w || !world || landing) return null;
  // D304: the daily gift rides on the welcome back, or has the card alone.
  const { title, amount, line, gift } = welcomeHead(w);
  const t = now();
  const barn = barnChip(world, idleContent, t);
  const close = () => {
    const card = document.querySelector('.juice-fanfare-card h2');
    if (card) flyCoins(card, { count: 10 });
    playSound('coin');
    useIdle.getState().closeWelcome();
  };
  // D335: at most three goals by name; the rest point at the Almanac's goals.
  const { shown, more } = welcomeGoalsSummary(idleContent, world, goals);
  const openGoals = () => {
    close();
    useIdle.getState().setTab('almanac');
    requestAnimationFrame(() =>
      requestAnimationFrame(() =>
        document.querySelector('[data-testid="almanac-goals"]')?.scrollIntoView({ block: 'start' }),
      ),
    );
  };
  // D459: the card holds the amount, one sentence and at most three ruled facts: the weather, the
  // barn and the goals met. Days that bring a gift, finds, visitors or a new year say so in a
  // plain note under the list. The upgrade and what the barn cost are on the barn sheet.
  const weatherLine = w.weather
    ? `${weatherText(idleContent.bonuses.weather.factor, idleContent.bonuses.weather.durationMs)}.`
    : null;
  const finds = welcomeFinds(w);
  const visitors = welcomeVisitors(w, idleContent);
  const facts: MomentFact[] = [];
  if (weatherLine) facts.push({ value: weatherLine, testId: 'welcome-weather' });
  facts.push({ value: welcomeNext(barn.fullAt, t), testId: 'welcome-next' });
  if (shown.length > 0)
    facts.push({
      value: `Goals met: ${shown.join(', ')}${more > 0 ? ` and ${more} more` : ''}`,
      testId: 'welcome-goals',
    });
  const notes: Array<[string, string]> = [];
  if (gift) notes.push(['welcome-gift', gift]);
  if (finds) notes.push(['welcome-finds', finds]);
  if (visitors) notes.push(['welcome-visitors', visitors]);
  if (w.yearStarted !== undefined)
    notes.push(['welcome-year', `Year ${yearOfGeneration(world)} has started.`]);
  const note: JSX.CSSProperties = { margin: 0, font: '400 13px/1.35 var(--font-ui)' };
  return (
    <Fanfare
      title={title}
      amount={amount}
      testId="welcome-back"
      {...(line ? { sub: line } : {})}
      facts={facts}
      onClose={close}
      actions={
        <Button variant="primary" full onClick={close}>
          Close
        </Button>
      }
    >
      {notes.map(([id, text]) => (
        <p key={id} data-testid={id} style={{ ...note, textAlign: 'left' }}>
          {text}
        </p>
      ))}
      <span data-testid="welcome-goals-more" style={{ display: 'grid' }}>
        <LinkRow onClick={openGoals}>Open {term('tabAlmanac')}</LinkRow>
      </span>
      {w.capped && (
        <LinkRow
          testId="welcome-open-barn"
          onClick={() => {
            close();
            useIdle.getState().setBarnOpen(true);
          }}
        >
          Open the {term('barn')}
        </LinkRow>
      )}
    </Fanfare>
  );
}

export function IdleApp() {
  const tab = useIdle((s) => s.tab);
  const ready = useIdle((s) => !!s.world);
  // D292: photo mode hides every screen's own chrome; the 3D lane frames the shot.
  const photoMode = useIdle((s) => s.photoMode);
  // D454 step 3: arranging hides the tab bar and the goal strip; the mode bar takes the strip's place.
  const arranging = useIdle((s) => !!s.arrange);
  const pending = useIdle((s) => !!s.world?.festivalPending);
  const offline = useIdle((s) => s.offline);
  const firstRun = useIdle((s) => s.firstRun !== null);
  const newWorldOpen = useIdle((s) => s.newWorldOpen);
  // D460: each tab counts only what waits there for the player, never what could be bought.
  const badges = useBadges();
  const systemDark = useSystemDark();
  // The World tab is the one the prompts sit on the map for; a held festival has its own screen.
  const worldShown = (tab === 'world' || photoMode) && !pending;

  useEffect(() => {
    document.documentElement.setAttribute('data-theme', systemDark ? 'dark' : 'light');
  }, [systemDark]);

  // D425: the open world's theme picks its accent and coin colours (tokens.css).
  const worldTheme = useIdle((s) => s.theme);
  useEffect(() => {
    document.documentElement.setAttribute('data-world', worldTheme);
  }, [worldTheme]);

  useEffect(() => {
    installAudioUnlock();
    void useIdle.getState().init();
    const id = setInterval(() => {
      if (document.visibilityState !== 'hidden') useIdle.getState().tick();
    }, TICK_MS);
    const onVisible = () => {
      if (document.visibilityState === 'visible') useIdle.getState().resume();
    };
    document.addEventListener('visibilitychange', onVisible);
    return () => {
      clearInterval(id);
      document.removeEventListener('visibilitychange', onVisible);
    };
  }, []);

  return (
    <div
      className="idle-root"
      data-screen-label={idleTabs().find((t) => t.id === tab)?.label}
      style={{
        position: 'relative',
        display: 'flex',
        flexDirection: 'column',
        overflow: 'hidden',
        background: 'var(--bg)',
        color: 'var(--ink)',
        width: '100%',
        height: '100%',
        flex: 1,
        minHeight: 0,
      }}
    >
      {!ready && <UpdateBar top />}
      {ready ? (
        <>
          {!photoMode && <TopStrip />}
          {arranging && !photoMode && <ArrangeBar />}
          {!photoMode && <UpdateBar top={false} />}
          {!photoMode && <InstallPrompt />}
          {!photoMode && <SecurePrompt />}
          {/* D431: on the World tab the prompts are pills on the map; the other tabs keep strips. */}
          {!photoMode && !pending && !worldShown && <NextStrip />}
          {!photoMode && !worldShown && <BonusStrip />}
          {pending && !photoMode ? (
            // D276: a held festival has the screen until its new year starts.
            <FestivalPending />
          ) : tab === 'world' || photoMode ? (
            <WorldTab
              moreOffers={arranging ? undefined : <MoreOffersMap />}
              prompts={
                <>
                  <NextStrip pill />
                  <BonusStrip pill />
                </>
              }
            />
          ) : tab === 'region' ? (
            <RegionTab />
          ) : tab === 'almanac' ? (
            <AlmanacTab />
          ) : (
            <FestivalTab />
          )}
        </>
      ) : (
        <div style={{ flex: 1, display: 'grid', placeItems: 'center', color: 'var(--ink-2)' }}>
          {offline ? (
            <div style={{ display: 'grid', gap: 12, justifyItems: 'center', textAlign: 'center' }}>
              <span>The {term('world')} could not be reached.</span>
              <Button onClick={() => void useIdle.getState().init()}>Try again</Button>
            </div>
          ) : (
            `Opening the ${term('world')}`
          )}
        </div>
      )}
      {!photoMode && !arranging && (
        <TabBar
          tabs={idleTabs()}
          badges={{
            almanac: badges?.almanac.count || undefined,
            festival: badges?.festival.count || undefined,
            region: badges?.region.count || undefined,
          }}
          active={tab}
          onChange={(t) => {
            // D461: a tab opened from the bar goes to its first marked thing.
            if (t !== tab) arriveByTab();
            useIdle.getState().setTab(t as IdleTab);
          }}
        />
      )}
      {!photoMode && <OffersSheet />}
      {!photoMode && <BarnSheet />}
      {!photoMode && <SettingsSheet />}
      {!photoMode && <GuideScreen />}
      {!photoMode && <Welcome />}
      {!photoMode && <FestivalFanfare />}
      <JuiceLayer />
      <SignInScreen onSignedIn={() => useIdle.getState().init()} />
      {/* D266: no world yet, so the first run names one. */}
      {((!ready && firstRun) || newWorldOpen) && <FirstRun />}
      {/* D359: the landing scene, over everything while the app boots; it fades and goes. */}
      <Landing />
    </div>
  );
}
